"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useSearchParams } from "next/navigation";
import { viruses } from "@prisma/client";
import { getContextFromUrl, updateUrlContext } from "@/utils/contextUrl";

interface SelectContextType {
  loading: boolean;
  contextValue: viruses;
  setContextValue: (value: viruses) => void;
  isReadonly: boolean;
  setIsReadonly: (value: boolean) => void;
}

const defaultContextValue: SelectContextType = {
  loading: true,
  contextValue: "DENV",
  setContextValue: () => {
    console.warn("setContextValue not implemented");
  },
  isReadonly: false,
  setIsReadonly: () => {
    console.warn("setIsReadonly not implemented");
  },
};

const SelectContext = createContext<SelectContextType>(defaultContextValue);

export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const [contextRetrieved, setContextRetrieved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [contextValue, setContextValue] = useState<viruses>(
    defaultContextValue.contextValue
  );
  const [isReadonly, setIsReadonly] = useState<boolean>(
    defaultContextValue.isReadonly
  );

  // Retrieve context from URL on mount
  useEffect(() => {
    const contextFromUrl = getContextFromUrl(defaultContextValue.contextValue);
    setContextValue(contextFromUrl);
    sessionStorage.setItem("appContext", contextFromUrl);
    setContextRetrieved(true);
  }, []);

  // Sync URL whenever context changes programmatically
  useEffect(() => {
    if (contextRetrieved) {
      updateUrlContext(contextValue);
      sessionStorage.setItem("appContext", contextValue);
      setLoading(false);
    }
  }, [contextValue, contextRetrieved]);

  return (
    <SelectContext.Provider
      value={{
        loading,
        contextValue,
        setContextValue,
        isReadonly,
        setIsReadonly,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
};

// Hook to reuse the context
export const useSelect = (): SelectContextType => useContext(SelectContext);

/**
 * Syncs the global virus context with the `?context=` query parameter.
 */
export const useSyncContextFromSearchParams = (): boolean => {
  const { contextValue, setContextValue } = useSelect();
  const searchParams = useSearchParams();
  const [isSynced, setIsSynced] = useState(false);

  useEffect(() => {
    if (!searchParams) {
      return;
    }

    const contextFromUrl = searchParams.get("context");

    if (
      contextFromUrl &&
      Object.values(viruses).includes(contextFromUrl as viruses) &&
      contextFromUrl !== contextValue
    ) {
      setContextValue(contextFromUrl as viruses);
      return;
    }

    setIsSynced(true);
  }, [searchParams, contextValue, setContextValue]);

  return isSynced;
};
